/**
 * WatchlistRowView — one row of the "+ Add to Watchlist" dropdown (SE-5022),
 * in whichever of its two shapes the user's grant on that list allows.
 *
 * SE-8306: the fork is the whole point. An OWNER / EDITOR row is the original
 * pressable checkbox. A VIEWER row is still LISTED — a list a coworker shared
 * with you should be reachable here, which is why SE-5022 included it — but it
 * is inert and says "View only", because the backend gates both
 * `addPlayerToWatchlist` and `removePlayerFromWatchlist` on role and refuses a
 * VIEWER's write in either direction. A checkbox there advertised an action
 * that could never succeed.
 * @remarks View component — no business logic.
 */

import { CheckIcon } from "lucide-react-native";
import { memo } from "react";

import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
} from "@/components/atoms/Checkbox";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Single list shown in the dropdown */
export interface WatchlistDropdownItem {
  /** Watchlist id */
  readonly id: string;
  /** Display name */
  readonly name: string;
  /** Visible checked state (server membership ± in-flight optimistic toggle) */
  readonly isChecked: boolean;
  /**
   * Whether the user may change this list's membership (OWNER / EDITOR).
   * False for a VIEWER grant — the row then renders read-only (SE-8306).
   */
  readonly canEdit: boolean;
}

/** View props for one dropdown row */
export interface WatchlistRowViewProps {
  /** The list this row stands for */
  readonly item: WatchlistDropdownItem;
  /**
   * Toggle handler for this row. Undefined on a read-only row, where the
   * container builds no handler at all.
   */
  readonly onToggle?: () => Promise<void>;
}

/**
 * Render one dropdown row — toggleable when the grant allows a write,
 * read-only and labelled when it does not.
 * @param props - View props
 * @param props.item - The list this row stands for
 * @param props.onToggle - Toggle handler (editable rows only)
 */
const WatchlistRowView = ({ item, onToggle }: WatchlistRowViewProps) =>
  item.canEdit ? (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityLabel={item.name}
      accessibilityState={{ checked: item.isChecked }}
      className="web:cursor-pointer rounded-none hover:bg-surface-raised"
    >
      <Stack direction="horizontal" className="items-center gap-3 px-3 py-2">
        {/* The row Pressable above is the single semantic checkbox: it owns
         * the role, the name and the checked state. This Checkbox is the tick
         * mark only — decoration — so it must be inert in BOTH directions.
         *
         * TOUCH (`pointer-events-none`, SE-5432): the Gluestack Checkbox Root
         * is a Pressable with no onChange, so on native it captures taps on
         * the tick mark and silently drops them — the checkbox reads as dead
         * on device. Taps have to fall through to the row. SelectListRowView
         * has carried this since SE-5432; this row was missing it, which the
         * SE-8306 review caught.
         *
         * SEMANTICS (the three hide props): that same Pressable Root carries
         * its own `checkbox` role, so while it was exposed ONE row published
         * TWO checkboxes. Unnaming it is not enough on its own — a
         * CheckboxLabel child still computes the same name from its text,
         * which is why the list name renders as a sibling Text below rather
         * than through CheckboxLabel. That also makes both row shapes render
         * the name the same way. The three props are not redundant: iOS,
         * Android and react-native-web each read a different one. */}
        <Checkbox
          value={item.id}
          isChecked={item.isChecked}
          size="md"
          className="pointer-events-none"
          aria-hidden
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <CheckboxIndicator
            className={
              item.isChecked
                ? "border-status-info bg-status-info"
                : "border-outline-strong"
            }
          >
            <CheckboxIcon as={CheckIcon} className="text-content-inverse" />
          </CheckboxIndicator>
        </Checkbox>
        <Text variant="title-sm" className="text-content-primary">
          {item.name}
        </Text>
      </Stack>
    </Pressable>
  ) : (
    <Stack
      direction="horizontal"
      className="items-center justify-between gap-3 px-3 py-2"
    >
      <Text variant="title-sm" className="text-content-muted">
        {item.name}
      </Text>
      <Text variant="caption" className="text-content-subtle">
        View only
      </Text>
    </Stack>
  );

WatchlistRowView.displayName = "WatchlistRowView";

export default memo(WatchlistRowView);
